import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { dynamicParameters } from '@/utils/dynamicParameterUtil'
import allCountries from '~/utils/allCountries'
import { isTrue } from '~/utils/siteConfigChecker'

const convertMethodMapToArray = (methodMap) => {
  const reverseOrder = ['local', 'email', 'phone']
  const { default: preset, ...obj } = methodMap || {}
  return Object.keys(obj)
    .map((method, i) => ({
      name: method,
      value: obj[method],
      preset: preset ? method === preset : i === 0,
    }))
    .sort((a, b) => {
      return reverseOrder.indexOf(a.name) - reverseOrder.indexOf(b.name)
    })
    .reverse()
    .filter((method) => !!method.value)
}

export const useSiteConfigStore = defineStore('siteConfig', () => {
  // State
  const siteConfig = ref({})

  // ============ Getters (所有 computed 集中在 store) ============

  const getSiteConfig = computed(() => siteConfig.value)

  const affiliateCodeEnable = computed(() => siteConfig.value?.affiliateCodeEnable)

  const balancePrecision = computed(() => siteConfig.value?.balancePrecision ?? 0)

  const bgmEnable = computed(() => siteConfig.value?.bgmEnable || { pwa: false, web: false })

  const branchCodeEnable = computed(() => isTrue(siteConfig.value?.branchCodeEnable))

  const csChat = computed(() => siteConfig.value?.csChat || [])

  const countries = computed(() => {
    const { isGlobal = false, list = [], excludeList = [] } = siteConfig.value?.countryCode || {}
    if (isGlobal) {
      return allCountries.filter(
        (country) => !excludeList.some((item) => item.name.toLowerCase() === country.name && String(item.code) === country.code)
      )
    } else if (list.length > 0) {
      return list.map((item) => allCountries.find((country) => item.name.toLowerCase() === country.name && String(item.code) === country.code))
    } else {
      return []
    }
  })

  const diamondShopEnable = computed(() => isTrue(siteConfig.value?.diamondShopEnable))

  const demoGameEnable = computed(() => isTrue(siteConfig.value?.demoGameEnable))

  const dtVerify = computed(() => siteConfig.value?.dtVerify)

  const forgetPasswordEnable = computed(() => siteConfig.value?.forgetPasswordEnable)

  const gameInformationSummaryEnable = computed(() => siteConfig.value?.gameInformationSummaryEnable)

  const loginMethods = computed(() => {
    return convertMethodMapToArray(siteConfig.value?.loginMethods)
  })

  const registerMethods = computed(() => {
    return convertMethodMapToArray(siteConfig.value?.registerMethods)
  })

  const hasLogin = computed(() => {
    return loginMethods.value.filter((method) => !(method.name === 'guest' && method.value)).length
  })

  const hasRegister = computed(() => {
    return registerMethods.value.length
  })

  const hasGuest = computed(() => {
    return isTrue(siteConfig.value?.loginMethods?.guest)
  })

  const registerBindingAffiliateCodeInstructionsEnable = computed(() => {
    return siteConfig.value?.registerBindingAffiliateCodeInstructionsEnable
  })

  const locale = computed(() => {
    return siteConfig.value?.locale || 'en-US'
  })

  const lang = computed(() => {
    const { i18nLang } = useRuntimeConfig().public
    return siteConfig.value?.lang || i18nLang || ['en-US']
  })

  const bonusPreviewEnable = computed(() => isTrue(siteConfig.value?.bonusPreviewEnable))

  const loginPageAdBlock = computed(() => siteConfig.value?.loginPageAdBlock || {})

  const nicknameRegex = computed(() => siteConfig.value?.nicknameRegex)

  const nicknameRegexErrMsg = computed(() => siteConfig.value?.nicknameRegexErrMsg)

  const otpCodeLength = computed(() => +(siteConfig.value?.otpCodeLength || 0))

  const otpValidateEndpoint = computed(() => siteConfig.value?.otpValidateEndpoint)

  const bankValidateEndpoint = computed(() => siteConfig.value?.bankValidateEndpoint)

  const suspensionValidateEndpoint = computed(() => siteConfig.value?.suspensionValidateEndpoint || {})

  const currency = computed(() => siteConfig.value?.currency)

  const currencyConversionLimit = computed(() => parseFloat(siteConfig.value?.currencyConversionLimit || 0))

  const depositDefaultBonusPageEnable = computed(() => isTrue(siteConfig.value?.depositDefaultBonusPageEnable))

  const diamondShopExchangeMode = computed(() => siteConfig.value?.diamondShopExchangeMode)

  const giveUpActivityEnable = computed(() => isTrue(siteConfig.value?.giveUpActivityEnable))

  const poCurrencyEnable = computed(() => isTrue(siteConfig.value?.poCurrencyEnable))

  const promoCodeEnable = computed(() => isTrue(siteConfig.value?.promoCodeEnable))

  const promotionsRanking = computed(() => isTrue(siteConfig.value?.promotionsRanking))

  const reCaptcha = computed(() => siteConfig.value?.reCaptcha)

  const registerAutoPassOTPEnable = computed(() => siteConfig.value?.registerAutoPassOTPEnable)

  const registerForceBindingAffiliateCodeEnable = computed(() => siteConfig.value?.registerForceBindingAffiliateCodeEnable)

  const registerForceBindingBankEnable = computed(() => siteConfig.value?.registerForceBindingBankEnable)

  const registerForceBindingEmailEnable = computed(() => siteConfig.value?.registerForceBindingEmailEnable)

  const registerForceBindingPhoneEnable = computed(() => siteConfig.value?.registerForceBindingPhoneEnable)

  const registerForceNicknameEnable = computed(() => siteConfig.value?.registerForceNicknameEnable)

  const socialMedia = computed(() => {
    return (siteConfig.value?.socialMediaLink || [])
      .filter((item) => item.is_visible === true)
      .map((item) => {
        const actions = ['email', 'phone'].includes(item.icon_type) && item.link !== '#' ? 'COPY' : 'URL'
        return {
          ...item,
          actions,
        }
      })
  })

  const thirdPartyLoginMethods = computed(() => loginMethods.value.filter((item) => item.value?.callbackURL))

  const uniqueNicknameEnable = computed(() => siteConfig.value?.uniqueNicknameEnable)

  const urls = computed(() => siteConfig.value?.urls || {})

  const displayGameNameUnderIcon = computed(() => siteConfig.value?.displayGameNameUnderIcon)

  const selectWalletEnable = computed(() => isTrue(siteConfig.value?.selectWalletEnable))

  const checkActivityWalletRule = computed(() => siteConfig.value?.checkActivityWalletRule)

  const bankEnableQuantityLimit = computed(() => siteConfig.value?.bankEnableQuantityLimit)

  const doubleWalletWithdrawLimitCheck = computed(() => siteConfig.value?.doubleWalletWithdrawLimitCheck || '0')

  const timeZone = computed(() => siteConfig.value?.timeZone)

  const withdrawalServiceHours = computed(() => siteConfig.value?.withdrawalServiceHours)

  const missionDateTimeFormat = computed(() => siteConfig.value?.missionDateTimeFormat)

  const fanPage = computed(() => {
    const { socialMediaLink = [] } = siteConfig.value || {}
    const found = socialMediaLink.find((socialMedia) => socialMedia.fanPage)
    return found?.is_visible && found?.link ? found : null
  })

  const configClientURL = computed(() => {
    const pwaLink = `${siteConfig.value?.urls?.pwa}`
    if (!pwaLink.endsWith('/')) {
      return pwaLink.concat('/')
    }
    return pwaLink
  })

  const modifyNicknameEnable = computed(() => isTrue(siteConfig.value?.modifyNicknameEnable))

  const depositInformationButtonEnable = computed(() => isTrue(siteConfig.value?.depositInformationButtonEnable))

  const gameSearchEnable = computed(() => isTrue(siteConfig.value?.gameSearchEnable))

  const gameBackButtonMoveEnable = computed(() => isTrue(siteConfig.value?.gameBackButtonMoveEnable))

  const rouletteCollectMode = computed(() => isTrue(siteConfig.value?.rouletteCollectMode))

  const levelUpCalculateWallet = computed(() => siteConfig.value?.levelUpCalculateWallet || '2')

  // 交易編號輸入框 (0 => 不顯示, 1 => 選填, 2 => 必填)
  const depositTransactionIdOptional = computed(() => siteConfig.value?.depositTransactionIdOptional || '0')

  const bankDepositProofIsOptional = computed(() => siteConfig.value?.bankDepositProofIsOptional || '1')

  // 前台存款頁面銀行帳號全部隱碼（ true : 全部隱碼，false : 不隱碼)
  const depositBankAccountMasked = computed(() => isTrue(siteConfig.value?.depositBankAccountMasked))

  const singleHolderName = computed(() => isTrue(siteConfig.value?.singleHolderName))

  const singleCNIC = computed(() => isTrue(siteConfig.value?.singleCNIC))

  const pixType = computed(() => siteConfig.value?.pixType || [])

  const multiLoginBindEnable = computed(() => isTrue(siteConfig.value?.multiLoginBindEnable))

  const getDownloadLink = computed(() => {
    return (params) => {
      const deepLink = siteConfig.value?.urls?.deepLink
      if (!deepLink) return ''

      const url = new URL(deepLink)
      const downloadLink = `${url.origin}/download`

      // 取得網址中的 query 參數
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
      const urlParamsObj = Object.fromEntries(urlParams.entries())

      // 合併參數，parameterObj 優先（去掉重複的參數）
      const mergedParams = { ...urlParamsObj, ...params }

      // 產生參數字串
      const paramString = dynamicParameters(mergedParams)

      // 正確處理 ? 和 & 串接
      return paramString ? `${downloadLink}/?${paramString}` : `${downloadLink}/`
    }
  })

  // 保留舊的 getter 名稱以保持向後相容
  const getFanPage = fanPage
  const getConfigClientURL = configClientURL
  const getSocialList = socialMedia

  // ============ Actions ============

  function setSiteConfig(data) {
    siteConfig.value = data
  }

  // ============ Return ============

  return {
    // State
    siteConfig,

    // Getters
    getSiteConfig,
    affiliateCodeEnable,
    balancePrecision,
    bankDepositProofIsOptional,
    bankEnableQuantityLimit,
    bankValidateEndpoint,
    bgmEnable,
    bonusPreviewEnable,
    branchCodeEnable,
    checkActivityWalletRule,
    configClientURL,
    countries,
    csChat,
    currency,
    currencyConversionLimit,
    demoGameEnable,
    depositBankAccountMasked,
    depositDefaultBonusPageEnable,
    depositInformationButtonEnable,
    depositTransactionIdOptional,
    diamondShopEnable,
    diamondShopExchangeMode,
    displayGameNameUnderIcon,
    doubleWalletWithdrawLimitCheck,
    dtVerify,
    fanPage,
    forgetPasswordEnable,
    gameBackButtonMoveEnable,
    gameInformationSummaryEnable,
    gameSearchEnable,
    getConfigClientURL,
    getDownloadLink,
    getFanPage,
    getSocialList,
    giveUpActivityEnable,
    hasGuest,
    hasLogin,
    hasRegister,
    levelUpCalculateWallet,
    lang,
    locale,
    loginMethods,
    loginPageAdBlock,
    missionDateTimeFormat,
    modifyNicknameEnable,
    multiLoginBindEnable,
    nicknameRegex,
    nicknameRegexErrMsg,
    otpCodeLength,
    otpValidateEndpoint,
    pixType,
    poCurrencyEnable,
    promoCodeEnable,
    promotionsRanking,
    reCaptcha,
    registerAutoPassOTPEnable,
    registerBindingAffiliateCodeInstructionsEnable,
    registerForceBindingAffiliateCodeEnable,
    registerForceBindingBankEnable,
    registerForceBindingEmailEnable,
    registerForceBindingPhoneEnable,
    registerForceNicknameEnable,
    registerMethods,
    rouletteCollectMode,
    selectWalletEnable,
    singleCNIC,
    singleHolderName,
    socialMedia,
    suspensionValidateEndpoint,
    thirdPartyLoginMethods,
    timeZone,
    uniqueNicknameEnable,
    urls,
    withdrawalServiceHours,

    // Actions
    setSiteConfig,
  }
})
